import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Sheet, SheetContent, SheetDescription, SheetHeader, SheetTitle, SheetTrigger } from "@/components/ui/sheet";
import { 
  HelpCircleIcon, 
  MessageSquareIcon, 
  BookOpenIcon, 
  FileTextIcon,
  PhoneIcon,
  MailIcon,
  ExternalLinkIcon,
  SettingsIcon,
  BarChart3Icon,
  UsersIcon
} from "lucide-react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Separator } from "@/components/ui/separator";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { getBusinessHoursStatus } from "@/utils/businessHours";
import ChatRequestDialog from "@/components/ChatRequestDialog";
import ChatWidget from "@/components/ChatWidget";
import WhatsAppSupportDialog from "@/components/WhatsAppSupportDialog";
import { getApiEndpoint } from "@/lib/api";
import { getToken, getUser } from "@/lib/auth-storage";
import { useToast } from "@/hooks/use-toast";
import { useLanguage } from "@/contexts/LanguageContext";

const FloatingHelp = () => {
  const { t } = useLanguage();
  const [open, setOpen] = useState(false);
  const [chatDialogOpen, setChatDialogOpen] = useState(false);
  const [whatsappDialogOpen, setWhatsappDialogOpen] = useState(false);
  const [activeChatId, setActiveChatId] = useState<number | null>(null);
  const [user, setUser] = useState<any>(null);
  const [systemTicketForm, setSystemTicketForm] = useState({
    title: "",
    description: "",
    impact: "medium",
    urgency: "medium"
  });
  const [systemTicketSubmitting, setSystemTicketSubmitting] = useState(false);
  const [systemTickets, setSystemTickets] = useState<any[]>([]);
  const [systemTicketsLoading, setSystemTicketsLoading] = useState(false);
  const businessHours = getBusinessHoursStatus();
  const { toast } = useToast();

  useEffect(() => {
    const checkUser = () => {
      const userData = getUser();
      if (userData) {
        try {
          const parsedUser = JSON.parse(userData);
          setUser(parsedUser);
        } catch (error) {
          console.error("Erro ao parsear usuário:", error);
          setUser(null);
        }
      } else {
        setUser(null);
      }
    };

    // Verificar imediatamente
    checkUser();

    // Verificar mudanças no localStorage a cada segundo (similar ao Header)
    const interval = setInterval(checkUser, 1000);

    // Listener para eventos customizados de mudança de usuário
    const handleStorageChange = (e: StorageEvent) => {
      if (e.key === "user") {
        checkUser();
      }
    };

    // Listener para evento customizado (disparado quando login acontece)
    const handleUserChange = () => {
      checkUser();
    };

    window.addEventListener("storage", handleStorageChange);
    window.addEventListener("userChanged", handleUserChange);

    return () => {
      clearInterval(interval);
      window.removeEventListener("storage", handleStorageChange);
      window.removeEventListener("userChanged", handleUserChange);
    };
  }, []);

  const isAdmin = user?.role === "admin";
  const isInternal = user?.role === "internal" || user?.role === "analyst" || user?.role === "admin";
  const canOpenSystemTicket = !!user?.company_id && (user?.role === "internal" || user?.role === "analyst" || user?.role === "admin");

  useEffect(() => {
    const fetchSystemTickets = async () => {
      if (!open || !canOpenSystemTicket || !user?.id) return;
      try {
        setSystemTicketsLoading(true);
        const token = getToken();
        const response = await fetch(getApiEndpoint("/api/tickets?limit=200"), {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        });
        if (response.ok) {
          const data = await response.json();
          const filtered = (data?.tickets || [])
            .filter((ticket: any) => ticket?.requester?.id === user.id)
            .filter((ticket: any) => ticket?.custom_fields?.system_issue || ticket?.category === "Sistema");
          const sorted = filtered.sort((a: any, b: any) => {
            const aTime = new Date(a.created_at || 0).getTime();
            const bTime = new Date(b.created_at || 0).getTime();
            return bTime - aTime;
          });
          setSystemTickets(sorted.slice(0, 5));
        }
      } catch (error) {
        console.error("Erro ao buscar chamados do sistema:", error);
      } finally {
        setSystemTicketsLoading(false);
      }
    };

    fetchSystemTickets();
  }, [open, canOpenSystemTicket, user?.id]);

  const handleCreateSystemTicket = async () => {
    if (!systemTicketForm.title.trim() || !systemTicketForm.description.trim()) {
      toast({
        title: t.floatingHelp.requiredFields,
        description: t.floatingHelp.requiredFieldsDesc,
        variant: "destructive",
      });
      return;
    }

    try {
      setSystemTicketSubmitting(true);
      const token = getToken();
      if (!token) {
        throw new Error("Token de autenticação não encontrado.");
      }

      const payload = {
        title: systemTicketForm.title.trim(),
        description: systemTicketForm.description.trim(),
        type: "incident",
        category: "Sistema",
        subcategory: "Problema na plataforma",
        impact: systemTicketForm.impact,
        urgency: systemTicketForm.urgency,
        custom_fields: {
          system_issue: true
        }
      };

      const response = await fetch(getApiEndpoint("/api/tickets"), {
        method: "POST",
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
        body: JSON.stringify(payload),
      });

      const data = await response.json().catch(() => ({}));
      if (!response.ok) {
        throw new Error(data?.error || "Não foi possível abrir o chamado.");
      }

      setSystemTicketForm({
        title: "",
        description: "",
        impact: "medium",
        urgency: "medium"
      });
      setOpen(true);

      toast({
        title: t.floatingHelp.ticketSent,
        description: t.floatingHelp.ticketSentDesc(data?.ticket?.id || ""),
      });
    } catch (error: any) {
      toast({
        title: t.floatingHelp.error,
        description: error?.message || t.floatingHelp.errorSendDesc,
        variant: "destructive",
      });
    } finally {
      setSystemTicketSubmitting(false);
    }
  };

  const quickLinks = isAdmin ? [
    { title: t.floatingHelp.adminPanel, description: t.floatingHelp.adminPanelDesc, icon: SettingsIcon, href: "/admin", color: "text-purple-600" },
    { title: t.floatingHelp.service, description: t.floatingHelp.serviceDesc, icon: MessageSquareIcon, href: "/atendimento", color: "text-blue-600" },
    { title: t.floatingHelp.reports, description: t.floatingHelp.reportsDesc, icon: BarChart3Icon, href: "/relatorios", color: "text-green-600" },
    { title: t.floatingHelp.manageUsers, description: t.floatingHelp.manageUsersDesc, icon: UsersIcon, href: "/admin", color: "text-orange-600" },
    { title: t.floatingHelp.faq, description: t.floatingHelp.faqDesc, icon: HelpCircleIcon, href: "/faq", color: "text-indigo-600" }
  ] : isInternal ? [
    { title: t.floatingHelp.adminPanel, description: t.floatingHelp.adminPanelDescInternal, icon: SettingsIcon, href: "/admin", color: "text-purple-600" },
    { title: t.floatingHelp.service, description: t.floatingHelp.serviceDesc, icon: MessageSquareIcon, href: "/atendimento", color: "text-blue-600" },
    { title: t.floatingHelp.reports, description: t.floatingHelp.reportsDesc, icon: BarChart3Icon, href: "/relatorios", color: "text-green-600" },
    { title: t.floatingHelp.manageUsers, description: t.floatingHelp.manageUsersDescInternal, icon: UsersIcon, href: "/admin", color: "text-orange-600" },
    { title: t.floatingHelp.faq, description: t.floatingHelp.faqDesc, icon: HelpCircleIcon, href: "/faq", color: "text-indigo-600" }
  ] : [
    { title: t.floatingHelp.howToOpenTicket, description: t.floatingHelp.howToOpenTicketDesc, icon: FileTextIcon, href: "/novo-chamado", color: "text-blue-600" },
    { title: t.floatingHelp.faq, description: t.floatingHelp.faqDesc, icon: HelpCircleIcon, href: "/faq", color: "text-green-600" },
    { title: t.floatingHelp.servicePolicy, description: t.floatingHelp.servicePolicyDesc, icon: BookOpenIcon, href: "/politica-de-servico", color: "text-purple-600" },
    { title: t.floatingHelp.myTickets, description: t.floatingHelp.myTicketsDesc, icon: MessageSquareIcon, href: "/chamados", color: "text-orange-600" }
  ];

  const contactInfo = [
    { type: t.floatingHelp.email, value: "suporte@tecitbettersolution.com.br", icon: MailIcon, href: "mailto:suporte@tecitbettersolution.com.br" },
    { type: t.floatingHelp.phone, value: "(11) 98546-4552", icon: PhoneIcon, href: "tel:+5511985464552" }
  ];

  const businessHoursMessage = businessHours.isBusinessHours
    ? t.businessHours.messageUntil(businessHours.nextBusinessHour.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" }))
    : t.businessHours.messageBot(businessHours.nextBusinessHour.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" }));

  return (
    <div className="fixed bottom-6 right-6 z-50">
      <Sheet open={open} onOpenChange={setOpen}>
        <SheetTrigger asChild>
          <Button
            size="lg"
            className="h-14 w-14 rounded-full bg-blue-600 hover:bg-blue-700 shadow-lg hover:shadow-xl transition-all duration-300 hover:scale-110"
            aria-label={t.floatingHelp.ariaLabel}
          >
            <HelpCircleIcon className="h-6 w-6 text-white" />
          </Button>
        </SheetTrigger>
        <SheetContent side="right" className="w-full sm:w-[400px] overflow-y-auto dark:bg-gray-900 dark:border-gray-800">
          <SheetHeader>
            <SheetTitle className="flex items-center gap-2 dark:text-white">
              <HelpCircleIcon className="h-5 w-5 text-blue-600 dark:text-blue-400" />
              {t.floatingHelp.title}
            </SheetTitle>
            <SheetDescription className="dark:text-gray-300">
              {t.floatingHelp.description}
            </SheetDescription>
          </SheetHeader>

          <div className="mt-6 space-y-6">
            {/* Status de Atendimento */}
            <Card className="border-l-4 border-l-blue-600 dark:bg-gray-800/50 dark:border-gray-700 dark:border-l-blue-500">
              <CardHeader className="pb-3">
                <CardTitle className="text-sm font-medium flex items-center gap-2 dark:text-gray-100">
                  {businessHours.isBusinessHours ? (
                    <>
                      <div className="h-2 w-2 rounded-full bg-green-500" />
                      <button 
                        onClick={() => setWhatsappDialogOpen(true)}
                        className="hover:underline text-left"
                      >
                        {t.floatingHelp.humanAvailable}
                      </button>
                    </>
                  ) : (
                    <>
                      <div className="h-2 w-2 rounded-full bg-orange-500" />
                      {t.floatingHelp.outsideHours}
                    </>
                  )}
                </CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-xs text-gray-600 dark:text-gray-400">
                  {businessHoursMessage}
                </p>
              </CardContent>
            </Card>

            <Separator className="dark:bg-gray-700" />

            {canOpenSystemTicket && (
              <>
                <Card className="border-l-4 border-l-purple-600 dark:bg-gray-800/50 dark:border-gray-700 dark:border-l-purple-500">
                  <CardHeader className="pb-2">
                    <CardTitle className="text-sm font-medium dark:text-gray-100">{t.floatingHelp.systemSupport}</CardTitle>
                    <CardDescription className="dark:text-gray-400">
                      {t.floatingHelp.systemSupportDesc}
                    </CardDescription>
                  </CardHeader>
                  <CardContent className="space-y-3">
                    <div className="space-y-2">
                      <Label htmlFor="system-ticket-title" className="dark:text-gray-200">{t.floatingHelp.titleLabel}</Label>
                      <Input
                        id="system-ticket-title"
                        placeholder={t.floatingHelp.titlePlaceholder}
                        value={systemTicketForm.title}
                        onChange={(e) => setSystemTicketForm((prev) => ({ ...prev, title: e.target.value }))}
                        className="dark:border-gray-600 dark:bg-gray-700 dark:text-gray-100"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="system-ticket-description" className="dark:text-gray-200">{t.floatingHelp.descriptionLabel}</Label>
                      <Textarea
                        id="system-ticket-description"
                        placeholder={t.floatingHelp.descriptionPlaceholder}
                        value={systemTicketForm.description}
                        onChange={(e) => setSystemTicketForm((prev) => ({ ...prev, description: e.target.value }))}
                        rows={3}
                        className="dark:border-gray-600 dark:bg-gray-700 dark:text-gray-100"
                      />
                    </div>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                      <div className="space-y-2">
                        <Label htmlFor="system-ticket-impact" className="dark:text-gray-200">{t.floatingHelp.impactLabel}</Label>
                        <Select
                          value={systemTicketForm.impact}
                          onValueChange={(value) => setSystemTicketForm((prev) => ({ ...prev, impact: value }))}
                        >
                          <SelectTrigger id="system-ticket-impact">
                            <SelectValue placeholder={t.floatingHelp.impactLabel} />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="critical">{t.floatingHelp.impactCritical}</SelectItem>
                            <SelectItem value="high">{t.floatingHelp.impactHigh}</SelectItem>
                            <SelectItem value="medium">{t.floatingHelp.impactMedium}</SelectItem>
                            <SelectItem value="low">{t.floatingHelp.impactLow}</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="system-ticket-urgency" className="dark:text-gray-200">{t.floatingHelp.urgencyLabel}</Label>
                        <Select
                          value={systemTicketForm.urgency}
                          onValueChange={(value) => setSystemTicketForm((prev) => ({ ...prev, urgency: value }))}
                        >
                          <SelectTrigger id="system-ticket-urgency">
                            <SelectValue placeholder={t.floatingHelp.urgencyLabel} />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="critical">{t.floatingHelp.impactCritical}</SelectItem>
                            <SelectItem value="high">{t.floatingHelp.impactHigh}</SelectItem>
                            <SelectItem value="medium">{t.floatingHelp.impactMedium}</SelectItem>
                            <SelectItem value="low">{t.floatingHelp.impactLow}</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                    </div>
                    <Button onClick={handleCreateSystemTicket} disabled={systemTicketSubmitting} className="w-full">
                      {systemTicketSubmitting ? t.floatingHelp.sending : t.floatingHelp.sendTicketToSupport}
                    </Button>
                  </CardContent>
                </Card>

                <Card className="border-l-4 border-l-gray-200 dark:bg-gray-800/50 dark:border-gray-700 dark:border-l-gray-600">
                  <CardHeader className="pb-2">
                    <CardTitle className="text-sm font-medium dark:text-gray-100">{t.floatingHelp.mySystemTickets}</CardTitle>
                    <CardDescription className="dark:text-gray-400">
                      {t.floatingHelp.mySystemTicketsDesc}
                    </CardDescription>
                  </CardHeader>
                  <CardContent className="space-y-2">
                    {systemTicketsLoading && (
                      <p className="text-xs text-gray-500 dark:text-gray-400">{t.floatingHelp.loadingTickets}</p>
                    )}
                    {!systemTicketsLoading && systemTickets.length === 0 && (
                      <p className="text-xs text-gray-500 dark:text-gray-400">{t.floatingHelp.noTicketsYet}</p>
                    )}
                    {!systemTicketsLoading && systemTickets.length > 0 && (
                      <div className="space-y-2">
                        {systemTickets.map((ticket) => (
                          <Link
                            key={ticket.id}
                            to={`/atendimento/chamado/${ticket.id}`}
                            onClick={() => setOpen(false)}
                            className="flex items-center justify-between rounded-md border border-gray-200 dark:border-gray-600 px-3 py-2 text-xs hover:border-gray-300 hover:bg-gray-50 dark:hover:border-gray-500 dark:hover:bg-gray-700/50"
                          >
                            <div className="flex flex-col">
                              <span className="font-medium text-gray-800 dark:text-gray-200">{ticket.title}</span>
                              <span className="text-[11px] text-gray-500 dark:text-gray-400">#{ticket.id}</span>
                            </div>
                            <span className="text-[11px] text-gray-600 dark:text-gray-400">
                              {ticket.status === "open"
                                ? t.dashboard.statusOpen
                                : ticket.status === "in_progress"
                                ? t.dashboard.statusInProgress
                                : ticket.status === "resolved"
                                ? t.dashboard.statusResolved
                                : ticket.status === "closed"
                                ? t.dashboard.statusClosed
                                : ticket.status}
                            </span>
                          </Link>
                        ))}
                      </div>
                    )}
                  </CardContent>
                </Card>

                <Separator className="dark:bg-gray-700" />
              </>
            )}

            {/* Links Rápidos */}
            <div>
              <h3 className="text-sm font-semibold text-gray-900 dark:text-white mb-3">{t.floatingHelp.quickAccess}</h3>
              <div className="space-y-2">
                {quickLinks.map((link, index) => {
                  const Icon = link.icon;
                  return (
                    <Link
                      key={index}
                      to={link.href}
                      onClick={() => setOpen(false)}
                      className="flex items-center gap-3 p-3 rounded-lg border border-gray-200 dark:border-gray-600 dark:bg-gray-800/50 group hover:bg-gray-50 dark:hover:bg-gray-700/50"
                    >
                      <div className={`p-2 rounded-lg bg-gray-100 dark:bg-gray-700 group-hover:bg-blue-100 dark:group-hover:bg-blue-900/30 ${link.color} dark:opacity-90`}>
                        <Icon className="h-4 w-4" />
                      </div>
                      <div className="flex-1">
                        <p className="text-sm font-medium text-gray-900 dark:text-gray-100">{link.title}</p>
                        <p className="text-xs text-gray-500 dark:text-gray-400">{link.description}</p>
                      </div>
                      <ExternalLinkIcon className="h-4 w-4 text-gray-400 dark:text-gray-500 group-hover:text-blue-600 dark:group-hover:text-blue-400" />
                    </Link>
                  );
                })}
              </div>
            </div>

            <Separator className="dark:bg-gray-700" />

            {/* Contato */}
            <div>
              <h3 className="text-sm font-semibold text-gray-900 dark:text-white mb-3">{t.floatingHelp.contactUs}</h3>
              <div className="space-y-2">
                {contactInfo.map((contact, index) => {
                  const Icon = contact.icon;
                  return (
                    <a
                      key={index}
                      href={contact.href}
                      className="flex items-center gap-3 p-3 rounded-lg border border-gray-200 dark:border-gray-600 dark:bg-gray-800/50 group hover:bg-gray-50 dark:hover:bg-gray-700/50"
                    >
                      <div className="p-2 rounded-lg bg-gray-100 dark:bg-gray-700 group-hover:bg-blue-100 dark:group-hover:bg-blue-900/30 text-blue-600 dark:text-blue-400">
                        <Icon className="h-4 w-4" />
                      </div>
                      <div className="flex-1">
                        <p className="text-sm font-medium text-gray-900 dark:text-gray-100">{contact.type}</p>
                        <p className="text-xs text-gray-600 dark:text-gray-400">{contact.value}</p>
                      </div>
                    </a>
                  );
                })}
              </div>
            </div>

            <Separator className="dark:bg-gray-700" />

            {!isAdmin && !isInternal && (
              <>
                <Button
                  onClick={() => {
                    setOpen(false);
                    setChatDialogOpen(true);
                  }}
                  className="w-full bg-green-600 hover:bg-green-700 mb-2"
                >
                  <MessageSquareIcon className="h-4 w-4 mr-2" />
                  {t.floatingHelp.chatWithAdmin}
                </Button>

                <Link to="/novo-chamado" onClick={() => setOpen(false)}>
                  <Button className="w-full bg-blue-600 hover:bg-blue-700">
                    <FileTextIcon className="h-4 w-4 mr-2" />
                    {t.floatingHelp.createNewTicket}
                  </Button>
                </Link>
              </>
            )}

            {isAdmin && (
              <Link to="/admin" onClick={() => setOpen(false)}>
                <Button className="w-full bg-purple-600 hover:bg-purple-700">
                  <SettingsIcon className="h-4 w-4 mr-2" />
                  {t.floatingHelp.adminPanel}
                </Button>
              </Link>
            )}

            {isInternal && !isAdmin && (
              <Link to="/atendimento" onClick={() => setOpen(false)}>
                <Button className="w-full bg-blue-600 hover:bg-blue-700">
                  <MessageSquareIcon className="h-4 w-4 mr-2" />
                  {t.floatingHelp.goToService}
                </Button>
              </Link>
            )}

            {/* Informações Adicionais */}
            <div className="pt-4 border-t dark:border-gray-700">
              <p className="text-xs text-gray-500 dark:text-gray-400 text-center">
                {t.floatingHelp.needMoreHelp}
              </p>
            </div>
          </div>
        </SheetContent>
      </Sheet>

      {/* Dialog de Solicitação de Chat */}
      <ChatRequestDialog
        open={chatDialogOpen}
        onOpenChange={setChatDialogOpen}
        onChatStarted={(chatId) => {
          setActiveChatId(chatId);
          setChatDialogOpen(false);
        }}
      />

      {/* Widget de Chat */}
      {activeChatId && (
        <ChatWidget
          chatId={activeChatId}
          onClose={() => setActiveChatId(null)}
          onChatEnded={() => setActiveChatId(null)}
        />
      )}

      {/* Dialog de Suporte WhatsApp */}
      <WhatsAppSupportDialog
        open={whatsappDialogOpen}
        onOpenChange={setWhatsappDialogOpen}
      />
    </div>
  );
};

export default FloatingHelp;

